// src/gestion-logistics/types/shipment.types.ts

export enum ShipmentStatus {
  DRAFT = "draft",
  CONFIRMED = "confirmed",
  IN_TRANSIT = "in_transit",
  DELIVERED = "delivered",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
}

export enum TransportMode {
  SEA = "sea",
  AIR = "air",
  ROAD = "road",
  RAIL = "rail",
  MULTIMODAL = "multimodal",
}

export type ShipmentDocumentType =
  | "bl"
  | "lta"
  | "cmr"
  | "commercial_invoice"
  | "packing_list"
  | "certificate_origin"
  | "customs_declaration"
  | "insurance"
  | "other";

export interface ShipmentDocumentFile {
  url?: string;
  key?: string;
  name?: string;
  mimetype?: string;
  size?: number;
}

export interface ShipmentDocument {
  id: string;
  type: ShipmentDocumentType;
  label?: string;
  reference?: string;
  document_date?: string;
  notes?: string;
  file?: ShipmentDocumentFile;
  uploaded_at?: string;
  uploaded_by?: {
    id?: number;
    name?: string;
  };
}

export interface Shipment {
  id: number;
  company_id: number;
  warehouse_id?: number;

  // Identification
  shipment_number: string;
  reference_client?: string;

  // Parties
  client_id?: number;
  client_name: string;
  consignee_name?: string;
  shipper_name?: string;
  receiver_name?: string;
  receiver_address?: string;

  // Itinéraire
  origin_country?: string;
  origin_city?: string;
  origin_address?: string;
  port_of_loading?: string;
  destination_country?: string;
  destination_city?: string;
  destination_address?: string;
  port_of_discharge?: string;
  final_destination?: string;

  // Transport
  transport_mode: TransportMode;
  service_type?: string;
  carrier_name?: string;
  vessel_name?: string;
  voyage_number?: string;
  clearing_agent_id?: number;
  clearing_agent_name?: string;

  // Marchandise
  goods_description?: string;
  total_weight?: number;
  total_volume?: number;
  number_of_packages?: number;
  incoterm?: string;

  // Statut et dates
  status: ShipmentStatus;
  booking_date?: string;
  etd?: string; // Estimated Time of Departure
  eta?: string; // Estimated Time of Arrival
  actual_departure_date?: string;
  actual_arrival_date?: string;
  completion_date?: string;

  // Documents
  bl_number?: string;
  container_numbers?: string;

  // Financier
  total_cost: number;
  total_revenue: number;
  profit_margin: number;
  currency_id?: number;
  currency_name?: string;
  currency_symbol?: string;

  // Notes
  internal_notes?: string;
  attachments?: ShipmentDocument[] | string | null;

  // Audit
  created_by: number;
  created_at: string;
  updated_by?: number;
  updated_at?: string;

  // Relations (chargées séparément)
  job_costing?: any[];
  invoices?: any[];
  supplier_bills?: any[];
  containers?: Container[];
  packages?: Package[];
}

// =====================================================================
// NOUVEAUX TYPES: Conteneurs et Colis
// =====================================================================

export enum ContainerSizeFeet {
  TWENTY = "20",
  FORTY = "40",
  FORTYFIVE = "45",
}

export enum ContainerHeightType {
  STANDARD = "standard",
  HIGH_CUBE = "high_cube",
}

export enum ContainerStatus {
  EMPTY = "empty",
  LOADED = "loaded",
  IN_TRANSIT = "in_transit",
  DELIVERED = "delivered",
  RETURNED = "returned",
}

export interface Container {
  id?: number;
  company_id?: number;
  shipment_id?: number;

  // Identification
  container_number?: string;
  container_type?: string;
  seal_number?: string;

  // Caractéristiques physiques
  weight?: number;
  volume?: number;
  tare_weight?: number;
  gross_weight?: number;
  packages_count?: number;
  description?: string;

  // Type de conteneur
  size_feet?: ContainerSizeFeet;
  height_type?: ContainerHeightType;
  is_reefer?: boolean;
  temperature?: number;

  // Statut
  status?: ContainerStatus;
  display_order?: number;

  // Audit
  created_by?: number;
  created_at?: string;
  updated_by?: number;
  updated_at?: string;
}

export interface Package {
  id?: number;
  company_id?: number;
  shipment_id?: number;
  container_id?: number;

  // Identification
  package_number?: string;
  tracking_number?: string;

  // Caractéristiques physiques
  description?: string;
  weight?: number;
  volume?: number;
  dimensions?: string;

  // Propriétaire (pour groupage)
  owner_client_id?: number;
  owner_client_name?: string;

  // Classification
  package_type?: string;
  hs_code?: string;
  value?: number;
  currency_id?: number;

  // Conditions spéciales
  is_dangerous?: boolean;
  un_number?: string;
  is_fragile?: boolean;
  requires_temperature_control?: boolean;

  // Ordre
  display_order?: number;

  // Audit
  created_by?: number;
  created_at?: string;
  updated_by?: number;
  updated_at?: string;
}

export interface ShipmentFormData
  extends Omit<
    Shipment,
    | "id"
    | "shipment_number"
    | "total_cost"
    | "total_revenue"
    | "profit_margin"
    | "created_by"
    | "created_at"
    | "job_costing"
    | "invoices"
    | "supplier_bills"
  > {
  containers?: Container[];
  packages?: Package[];
}

export interface ShipmentFilters {
  search?: string;
  status?: ShipmentStatus;
  client_id?: number;
  date_from?: string;
  date_to?: string;
  transport_mode?: TransportMode;
}
